/**
 * Helpers for the shell's canonical `event` query param, which encodes both
 * the event id and the sidebar mode in a single value.
 *
 * Value scheme:
 *   - `123`        → details mode for event 123
 *   - `edit:123`   → composer edit mode for event 123
 *   - `new`        → composer create mode (no event id)
 *
 * Anything else (empty string, null, malformed prefix, lone colon) → null mode,
 * meaning the sidebar is closed.
 *
 * All shell code that reads or writes the `event` query param MUST go through
 * these helpers; never construct the value string inline.
 */
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var EDIT_PREFIX = 'edit:';
var CREATE_SENTINEL = 'new';
/**
 * Parse the raw `event` query param value into a structured `{ mode, eventId }`.
 * Returns `{ mode: null, eventId: null }` when the value is absent, empty, or
 * malformed.
 */
export var parseEventParam = function (value) {
    if (value === null || value === '') {
        return { mode: null, eventId: null };
    }
    if (value === CREATE_SENTINEL) {
        return { mode: 'create', eventId: null };
    }
    if (value.startsWith(EDIT_PREFIX)) {
        var eventId = value.slice(EDIT_PREFIX.length);
        if (eventId === '') {
            // `edit:` with no id is malformed → treat as closed sidebar
            return { mode: null, eventId: null };
        }
        return { mode: 'edit', eventId: eventId };
    }
    // No recognized prefix and not the create sentinel.
    // Reject values that look like a malformed mode-prefix (e.g. `:123`,
    // `random:abc`) so we don't render the sidebar with surprise state.
    if (value.includes(':')) {
        return { mode: null, eventId: null };
    }
    return { mode: 'details', eventId: value };
};
/**
 * Derive the shell-hosted event-composer / details sider state from a location
 * search string (e.g. `useLocation().search`).
 *
 * Router-agnostic on purpose: each MFE pins its own `react-router-dom` version,
 * so callers read the search via their own router and pass it here. App-level
 * `useShellComposer` / `useShellComposerOpen` hooks are thin wrappers over this.
 */
export var getShellComposerState = function (search) {
    var _a = parseEventParam(new URLSearchParams(search).get('event')), mode = _a.mode, eventId = _a.eventId;
    return { isOpen: mode !== null, mode: mode, eventId: eventId };
};
/**
 * Format a `{ mode, eventId }` pair back into the raw query param value.
 * Inverse of `parseEventParam` for all valid inputs.
 */
export var formatEventParam = function (args) {
    switch (args.mode) {
        case 'details':
            return args.eventId;
        case 'edit':
            return "".concat(EDIT_PREFIX).concat(args.eventId);
        case 'create':
            return CREATE_SENTINEL;
    }
};
/**
 * Build a full URL with the canonical `event` query param appended. Use this
 * any time you need to construct a link to open the sidebar above some MFE
 * canvas — never assemble path-style event URLs by hand.
 *
 * Existing query params on `basePath` are preserved; the `event` key is
 * replaced.
 *
 * When `args.mode === 'create'`, an optional `seed` may be passed to
 * pre-populate the composer's initial form values (start time, end time,
 * primary space). Seed fields are encoded as sibling query params
 * (`started_at`, `ended_at`, `space_id`) — the same names the shell already
 * reads in `buildInitialCreateEvent`, so any existing producer of these
 * URLs (email links, magic-link flows, etc.) keeps working. Seed params are
 * stripped for non-create modes — pass `null` (or omit) and any existing
 * seed params on `basePath` will be cleared, so the URL always describes a
 * self-consistent open intent.
 */
export var buildEventUrl = function (basePath, args, seed) {
    var value = formatEventParam(args);
    var _a = __read(basePath.split('?'), 2), path = _a[0], _b = _a[1], existingQuery = _b === void 0 ? '' : _b;
    var params = new URLSearchParams(existingQuery);
    params.set('event', value);
    // Every create-composer param (seed + direct prefill) is transient to a
    // single composer open. Strip them all up front so a freshly built URL
    // never inherits a previous open's title / invitee count / start time /
    // etc. For a create open that carries an explicit seed, re-apply just
    // that seed. Producers that intentionally prefill the composer (e.g.
    // `LegacyEventRedirect`) append their params *after* this call.
    clearCreateEventParams(params);
    if (args.mode === 'create' && seed) {
        applyCreateEventSeed(params, seed);
    }
    return "".concat(path, "?").concat(params.toString());
};
/**
 * Query-param keys used to encode a `CreateEventSeed`. These names match
 * the shell's pre-existing prefill contract (see `buildInitialCreateEvent`
 * in `EventOverlayHost`). Exported so callers that need to manipulate
 * `URLSearchParams` directly (e.g. inside a navigate call) can reference
 * the same names rather than hard-coding strings.
 */
export var CREATE_SEED_PARAM_KEYS = {
    start: 'started_at',
    end: 'ended_at',
    spaceId: 'space_id',
    levelId: 'level_id',
};
/**
 * Non-seed create-composer prefill params that the shell's `EventOverlayHost`
 * reads directly from the URL (see `buildInitialCreateEvent`). Unlike the seed
 * params these aren't round-tripped through a typed helper — producers (e.g.
 * `LegacyEventRedirect`) set them verbatim — but they're equally transient to a
 * single create-composer open and must be cleared when that open ends.
 * Otherwise a later open inherits a stale title / invitee count / etc. (the
 * "create another event and the previous title is still there" bug).
 */
export var CREATE_PREFILL_PARAM_KEYS = [
    'title',
    'invitee_count',
    'is_all_day',
    'is_private',
    'event_uid',
    'timezone',
];
/**
 * Extract a `CreateEventSeed` from a `URLSearchParams`. Returns an object
 * with only the fields that were present and well-formed; absent or
 * malformed fields are omitted. Never throws.
 */
export var parseCreateEventSeed = function (params) {
    var seed = {};
    var start = params.get(CREATE_SEED_PARAM_KEYS.start);
    if (start !== null && start !== '') {
        seed.startTimeISO = start;
    }
    var end = params.get(CREATE_SEED_PARAM_KEYS.end);
    if (end !== null && end !== '') {
        seed.endTimeISO = end;
    }
    var spaceId = params.get(CREATE_SEED_PARAM_KEYS.spaceId);
    if (spaceId !== null && spaceId !== '') {
        seed.spaceId = spaceId;
    }
    var levelId = params.get(CREATE_SEED_PARAM_KEYS.levelId);
    if (levelId !== null && levelId !== '') {
        seed.levelId = levelId;
    }
    return seed;
};
/**
 * Write a `CreateEventSeed` into a `URLSearchParams`, mutating it in place.
 * Fields that are absent from the seed (or empty) are removed from the
 * params — so calling this with a partial seed clears unused keys rather
 * than leaving stale values around.
 */
export var applyCreateEventSeed = function (params, seed) {
    if (seed.startTimeISO) {
        params.set(CREATE_SEED_PARAM_KEYS.start, seed.startTimeISO);
    }
    else {
        params.delete(CREATE_SEED_PARAM_KEYS.start);
    }
    if (seed.endTimeISO) {
        params.set(CREATE_SEED_PARAM_KEYS.end, seed.endTimeISO);
    }
    else {
        params.delete(CREATE_SEED_PARAM_KEYS.end);
    }
    if (seed.spaceId) {
        params.set(CREATE_SEED_PARAM_KEYS.spaceId, seed.spaceId);
    }
    else {
        params.delete(CREATE_SEED_PARAM_KEYS.spaceId);
    }
    if (seed.levelId) {
        params.set(CREATE_SEED_PARAM_KEYS.levelId, seed.levelId);
    }
    else {
        params.delete(CREATE_SEED_PARAM_KEYS.levelId);
    }
};
/**
 * Remove every create-composer param — both seed (`started_at` / `ended_at` /
 * `space_id` / `level_id`) and direct prefill (`title` / `invitee_count` /
 * `is_all_day` / `is_private` / `event_uid` / `timezone`) — from a
 * `URLSearchParams`. Call this when closing the composer or switching to a
 * non-create mode so stale values from one open never survive into the next.
 *
 * Clearing the prefill group (not just the seed) is what prevents the
 * "open a fresh event and the previous event's title is still there" bug:
 * the close path strips the seed, but the prefill params were being left in
 * the URL and re-read by the next create open.
 */
export var clearCreateEventParams = function (params) {
    var e_1, _a;
    params.delete(CREATE_SEED_PARAM_KEYS.start);
    params.delete(CREATE_SEED_PARAM_KEYS.end);
    params.delete(CREATE_SEED_PARAM_KEYS.spaceId);
    params.delete(CREATE_SEED_PARAM_KEYS.levelId);
    try {
        for (var CREATE_PREFILL_PARAM_KEYS_1 = __values(CREATE_PREFILL_PARAM_KEYS), CREATE_PREFILL_PARAM_KEYS_1_1 = CREATE_PREFILL_PARAM_KEYS_1.next(); !CREATE_PREFILL_PARAM_KEYS_1_1.done; CREATE_PREFILL_PARAM_KEYS_1_1 = CREATE_PREFILL_PARAM_KEYS_1.next()) {
            var key = CREATE_PREFILL_PARAM_KEYS_1_1.value;
            params.delete(key);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (CREATE_PREFILL_PARAM_KEYS_1_1 && !CREATE_PREFILL_PARAM_KEYS_1_1.done && (_a = CREATE_PREFILL_PARAM_KEYS_1.return)) _a.call(CREATE_PREFILL_PARAM_KEYS_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
};
